import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check, Truck, AlertTriangle, CreditCard, Package, Landmark,
  Zap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useGuestContext } from "@/context/GuestContext";
import { getProductVariants, submitReturnRequest } from "@/service/returnChatService";
import VariationSelectionModal from "@/components/customer/VariationSelectionModal";
import ResolutionConfirmationModal from "@/components/customer/ResolutionConfirmationModal";
import RefundSelectionModal from "@/components/customer/RefundSelectionModal";
import ResolutionSuccessModal from "@/components/customer/ResolutionSuccessModal";
import AddressSelectionModal, { AddressData } from "@/components/customer/AddressSelectionModal";
import { useCustomerContext } from "@/context/CustomerContext";

/**
 * FixResolution — Resolution page for "fit" category.
 *
 * Final Modular Flow:
 *   Selection Card (Replacement) -> Variation Modal -> Address Modal -> Confirmation Modal
 *   Selection Card (Refund)      -> Refund Modal
 */


const isValidImage = (url: any) => {
  if (!url || typeof url !== 'string') return false;
  return url.startsWith('http') || url.startsWith('/') || url.startsWith('data:');
};

const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`tw-relative tw-overflow-hidden tw-bg-[#1c1c1e] tw-rounded-md ${className}`}>
    <motion.div
      className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-transparent tw-via-white/5 tw-to-transparent"
      initial={{ x: "-100%" }}
      animate={{ x: "100%" }}
      transition={{
        duration: 1.6,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  </div>
);

const ResolutionPageSkeleton: React.FC = () => (
  <div className="tw-bg-[#131313] tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans">
    {/* Header Skeleton */}
    <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-justify-between tw-pb-8 tw-gap-4">
      <div className="tw-space-y-4 tw-flex-1">
        <Skeleton className="tw-h-3 tw-w-40" />
        <Skeleton className="tw-h-10 tw-w-64 md:tw-h-12" />
        <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
          <Skeleton className="tw-h-6 tw-w-24 tw-rounded-md" />
          <Skeleton className="tw-h-4 tw-w-64" />
        </div>
      </div>
    </div>

    <div className="tw-max-w-4xl tw-mx-auto tw-space-y-10 tw-mt-4">
      {/* Product Info Card Skeleton */}
      <div className="tw-bg-[#131313] tw-rounded-xl tw-p-6 tw-flex tw-items-center tw-gap-8 tw-border tw-border-white/5">
        <Skeleton className="tw-w-32 tw-h-32 tw-rounded-xl tw-flex-shrink-0" />
        <div className="tw-flex-1 tw-space-y-4">
          <Skeleton className="tw-h-3 tw-w-32" />
          <Skeleton className="tw-h-8 tw-w-64" />
          <Skeleton className="tw-h-4 tw-w-48" />
        </div>
      </div>

      <div className="tw-space-y-10">
        <div className="tw-text-center tw-max-w-2xl tw-mx-auto tw-space-y-4">
          <Skeleton className="tw-h-10 tw-w-96 tw-mx-auto" />
          <Skeleton className="tw-h-4 tw-w-64 tw-mx-auto" />
        </div>

        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="tw-bg-[#131313] tw-rounded-2xl tw-p-8 tw-space-y-8 tw-border tw-border-white/5">
              <Skeleton className="tw-w-14 tw-h-14 tw-rounded-xl" />
              <div className="tw-space-y-4">
                <Skeleton className="tw-h-8 tw-w-full" />
                <div className="tw-space-y-2">
                  <Skeleton className="tw-h-4 tw-w-3/4" />
                  <Skeleton className="tw-h-4 tw-w-1/2" />
                </div>
              </div>
              <Skeleton className="tw-h-12 tw-w-full tw-rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const FixResolution: React.FC = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const navigate = useNavigate();
  const { loginType } = useAuth();
  const { returnOnboardingData, fetchOnboardingData, isLoading: isContextLoading } = useGuestContext();
  const { refreshData } = useCustomerContext();
  
  // Find the correct product info matching the productId
  const productInfo = productId 
    ? returnOnboardingData?.product_info?.find((p: any) => p.id === productId)
    : returnOnboardingData?.product_info?.[0];

  const onboardingData = returnOnboardingData;
  const orderNumber = onboardingData?.order_details?.order_number || orderId?.substring(0, 8).toUpperCase();

  const [returnData, setReturnData] = useState<any>(null);
  const [variants, setVariants] = useState<any[]>([]);
  const [selectedColor, setSelectedColor] = useState<any>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedSolution, setSelectedSolution] = useState<string | null>(null);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showVariationModal, setShowVariationModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // Unified Success Modal State
  const [successModal, setSuccessModal] = useState<{
    isOpen: boolean;
    type: 'replacement' | 'store_credit' | 'refund';
  }>({ isOpen: false, type: 'replacement' });

  const [address, setAddress] = useState<AddressData>({
    fullName: "", street: "", city: "", state: "", pin: ""
  });

  useEffect(() => {
    if (!orderId) return;
    if (!returnOnboardingData) fetchOnboardingData(orderId);
  }, [orderId, returnOnboardingData, fetchOnboardingData]);

  useEffect(() => {
    if (returnOnboardingData?.returns) {
      const record = productId 
        ? returnOnboardingData.returns.find((r: any) => r.product_id === productId)
        : returnOnboardingData.returns[0];
      setReturnData(record || null);
    }
    if (returnOnboardingData?.shipping_details) {
      const s = returnOnboardingData.shipping_details;
      setAddress({
        fullName: s.name || "",
        street: [s.address_line_1, s.address_line_2].filter(Boolean).join(', ') || "",
        city: s.city || "",
        state: s.state || "",
        pin: s.zip || ""
      });
    }
  }, [returnOnboardingData]);

  useEffect(() => {
    const fetchVariants = async () => {
      if (!productInfo?.id) return;
      setIsLoadingVariants(true);
      try {
        const data = await getProductVariants(productInfo.id);
        if (data && data.length > 0) {
          setVariants(data);
          const allColors = data[0].colors || [];
          if (allColors.length > 0) {
            let initialColor = allColors[0];
            const currentVariantStr = (productInfo?.variant || "").toLowerCase();
            if (currentVariantStr) {
              const matched = allColors.find((c: any) => currentVariantStr.includes(c.name.toLowerCase()));
              if (matched) initialColor = matched;
            }
            setSelectedColor(initialColor);

            // Pre-select original size by searching for the size part in the variant string
            if (initialColor) {
              const currentVariantStr = productInfo?.variant || "";
              const allSizes = Object.keys(initialColor.sizes);
              const matchedSize = allSizes.find(sz => 
                currentVariantStr.toLowerCase().includes(` / ${sz.toLowerCase()}`) || 
                currentVariantStr.toLowerCase().endsWith(` ${sz.toLowerCase()}`)
              );
              
              if (matchedSize) {
                setSelectedSize(matchedSize);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching variants:", err);
      } finally {
        setIsLoadingVariants(false);
      }
    };
    fetchVariants();
  }, [productInfo?.id, productInfo?.variant]);


  const product = {
    image: productInfo?.image || "",
    title: productInfo?.title || "Product",
    price: productInfo?.price || "0",
    reason: returnData?.reason || "Fit / Size Issue",
  };

  const { originalColor, originalSize } = React.useMemo(() => {
    if (!productInfo?.variant) return { originalColor: null, originalSize: null };
    
    const parts = productInfo.variant.split(' / ').map((p: string) => p.trim());
    const isSize = (val: string) => /^(xs|s|m|l|xl|xxl|2xl|3xl|4xl|5xl|onesize|one size|\d+(\.\d+)?)$/i.test(val);

    let color = null;
    let size = null;

    if (parts.length === 1) {
      if (isSize(parts[0])) size = parts[0];
      else color = parts[0];
    } else if (parts.length >= 2) {
      if (isSize(parts[0]) && !isSize(parts[1])) {
        size = parts[0];
        color = parts[1];
      } else {
        color = parts[0];
        size = parts[1];
      }
    }
    return { originalColor: color, originalSize: size };
  }, [productInfo?.variant]);

  const goBack = () => {
    if (loginType === 'token' && orderId) {
      fetchOnboardingData(orderId, true);
    } else if (refreshData) {
      refreshData();
    }
    navigate(`/customer/orders/${orderId}`);
  };

  const goToOrderDetails = () => {
    if (loginType === 'token' && orderId) {
      fetchOnboardingData(orderId, true);
    } else if (refreshData) {
      refreshData();
    }
    navigate(`/customer/orders/${orderId}`);
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const placedDate = returnOnboardingData?.order_details?.placed_at || "2025-10-06T00:00:00Z";

  const handleFinalConfirm = async () => {
    if (!returnData?.id) return;
    
    // If not a refund, we need color/size
    if (!selectedSolution && (!selectedColor || !selectedSize)) return;

    setSubmitting(true);
    try {
      if (selectedSolution === "store_credit" || selectedSolution === "refund") {
        await submitReturnRequest(returnData.id, {
          selected_solution: selectedSolution,
          selected_refund_method: selectedSolution === "store_credit" ? "store_credit" : "original",
          status: "processing",
          reason: product.reason,
          customer_address: {
            name: address.fullName,
            address_line_1: address.street,
            city: address.city,
            state: address.state,
            zip: address.pin,
          },
        });
        setShowConfirmationModal(false);
        setSuccessModal({ isOpen: true, type: selectedSolution as any });
      } else {
        const variantId = selectedSize ? selectedColor.sizes[selectedSize] : null;
        const isSameProduct = (selectedColor.productId && String(selectedColor.productId) === String(productInfo?.shopify_product_id)) || !selectedColor.productId;

        await submitReturnRequest(returnData.id, {
          selected_solution: isSameProduct ? "exchange_same" : "exchange_different",
          replacement_product_id: selectedColor.productId || productInfo?.shopify_product_id,
          replacement_variant_id: String(variantId),
          status: "processing",
          reason: product.reason,
          customer_address: {
            name: address.fullName,
            address_line_1: address.street,
            city: address.city,
            state: address.state,
            zip: address.pin,
          },
        });
        setShowConfirmationModal(false);
        setSuccessModal({ isOpen: true, type: 'replacement' });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptStoreCredit = () => {
    setSelectedSolution("store_credit");
    setShowRefundModal(false);
    setShowAddressModal(true);
  };

  const handleAcceptOriginalRefund = () => {
    setSelectedSolution("refund");
    setShowRefundModal(false);
    setShowAddressModal(true);
  };

  if (isContextLoading || !returnOnboardingData) {
    return <ResolutionPageSkeleton />;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="tw-min-h-screen tw-p-6 md:tw-p-10 tw-font-sans">
      {/* Top Breadcrumb & Actions - Aligned with OrderDetails */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-justify-between tw-pb-8 tw-gap-4">
        <div className="tw-space-y-3">
          <div className="tw-text-xs tw-font-semibold tw-tracking-widest tw-uppercase tw-text-gray-500">
            <button onClick={goBack} className="hover:tw-text-gray-300 tw-transition-colors tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-inherit tw-font-inherit tw-uppercase">ORDERS</button>
            <span className="tw-mx-1">&gt;</span> 
            <span className="hover:tw-text-white tw-cursor-pointer tw-transition-colors" onClick={() => navigate(`/customer/orders/${orderId}`)}>#{orderNumber}</span>
            <span className="tw-mx-1">&gt;</span> 
            <span className="tw-text-[#00C5FB]">FIT ISSUE</span>
          </div>
          <h1 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-text-white tw-tracking-tight">
            Order #{orderNumber}
          </h1>
          <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-bg-[#00C5FB]/10 tw-text-[#00C5FB] tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-semibold tw-border tw-border-[#00C5FB]/20">
              <Zap className="tw-w-3.5 tw-h-3.5" />
              RESOLUTION READY
            </div>
            <span className="tw-text-sm tw-text-gray-400">Eligible for instant replacement or refund (Order Placed {formatDate(placedDate)})</span>
          </div>
        </div>
      </div>

      <div className="tw-max-w-4xl tw-mx-auto tw-space-y-10">
        {/* Product Info Card */}
        <div className="tw-bg-[#131313] tw-rounded-xl tw-p-6 tw-flex tw-items-center tw-gap-8 tw-border tw-border-white/5">
          <div className="tw-w-32 tw-h-32 tw-bg-white tw-rounded-xl tw-overflow-hidden tw-flex-shrink-0">
            {isValidImage(product.image) ? <img src={product.image} alt={product.title} className="tw-w-full tw-h-full tw-object-cover" /> : <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><Package className="tw-text-gray-400 tw-w-10 tw-h-10" /></div>}
          </div>
          <div className="tw-flex-1">
            <div className="tw-text-[10px] tw-font-black tw-tracking-[0.2em] tw-text-[#FDBA74] tw-uppercase tw-mb-2">Resolution Requested</div>
            <h2 className="tw-text-2xl tw-font-bold tw-text-white tw-mb-3">{product.title}</h2>
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-text-gray-400">
              <div className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-red-500/10 tw-flex tw-items-center tw-justify-center"><AlertTriangle className="tw-w-3 tw-h-3 tw-text-red-400" /></div>
              <span className="tw-text-sm">Reason: {product.reason}</span>
            </div>
          </div>
        </div>

          <div className="tw-space-y-10">
            <div className="tw-text-center tw-max-w-2xl tw-mx-auto">
              <h2 className="tw-text-3xl tw-font-bold tw-text-white tw-mb-4">How would you like to resolve this issue?</h2>
              <p className="tw-text-gray-400 tw-text-sm tw-leading-relaxed">Choose between an automated replacement or a full refund and settlement.</p>
            </div>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
              {/* Card 1: Replacement */}
              <div className="tw-bg-[#131313] tw-rounded-2xl tw-p-8 tw-flex tw-flex-col tw-gap-8 tw-border tw-border-white/5 tw-relative tw-overflow-hidden tw-group hover:tw-border-[#00C5FB]/30 tw-transition-all">
                <div className="tw-absolute tw-top-0 tw-right-0 tw-bg-[#00C5FB] tw-text-black tw-text-[9px] tw-font-black tw-px-4 tw-py-2 tw-rounded-bl-2xl tw-tracking-widest uppercase">RECOMMENDED</div>
                <div className="tw-w-14 tw-h-14 tw-rounded-xl tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center"><Truck className="tw-w-7 tw-h-7 tw-text-[#00C5FB]" /></div>
                <div className="tw-space-y-4">
                  <h3 className="tw-text-2xl tw-font-bold tw-text-white">Same Replacement</h3>
                  <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400"><Check className="tw-w-4 tw-h-4 tw-text-green-500" /><span className="tw-text-sm font-medium">Get a new, identical item</span></div>
                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400"><Package className="tw-w-4 tw-h-4 tw-text-gray-600" /><span className="tw-text-sm">Exchange size or color if needed</span></div>
                  </div>
                </div>
                <button onClick={() => setShowVariationModal(true)} className="tw-w-full tw-py-4 tw-rounded-xl tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-text-sm tw-border-none tw-cursor-pointer hover:tw-bg-cyan-400 tw-transition-all tw-shadow-[0_10px_20px_-5px_rgba(0,197,251,0.3)]">Proceed with Replacement</button>
              </div>

              {/* Card 2: Refund */}
              <div className="tw-bg-[#131313] tw-rounded-2xl tw-p-8 tw-flex tw-flex-col tw-gap-8 tw-border tw-border-white/5 hover:tw-border-white/10 tw-transition-all">
                <div className="tw-w-14 tw-h-14 tw-rounded-xl tw-bg-[#1c1c1e] tw-flex tw-items-center tw-justify-center"><CreditCard className="tw-w-6 tw-h-6 tw-text-[#FDBA74]" /></div>
                <div className="tw-space-y-4">
                  <h3 className="tw-text-2xl tw-font-bold tw-text-white">Get a Refund</h3>
                  <div className="tw-space-y-3">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400"><Check className="tw-w-4 tw-h-4 tw-text-gray-600" /><span className="tw-text-sm">Store Credit with <span className="tw-text-[#00C5FB]">5% extra bonus</span></span></div>
                    <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400"><Landmark className="tw-w-4 tw-h-4 tw-text-gray-600" /><span className="tw-text-sm">Refund to original bank account</span></div>
                  </div>
                </div>
                <button onClick={() => setShowRefundModal(true)} className="tw-w-full tw-py-4 tw-rounded-xl tw-bg-[#28282B] tw-text-white tw-font-bold tw-text-sm tw-border tw-border-white/10 tw-cursor-pointer hover:tw-bg-[#323235] tw-transition-all">Select Refund Method</button>
              </div>
            </div>
          </div>
      </div>

      {/* Variation Selection Modal */}
      <VariationSelectionModal 
        isOpen={showVariationModal}
        onClose={() => setShowVariationModal(false)}
        onConfirm={(color: any, size: string) => {
          setSelectedColor(color);
          setSelectedSize(size);
          setShowVariationModal(false);
          setShowAddressModal(true);
        }}
        variants={variants}
        initialColor={selectedColor}
        initialSize={selectedSize}
        originalColor={originalColor}
        originalSize={originalSize}
        productTitle={product.title}
        isLoading={isLoadingVariants}
      />

      {/* Address Selection Modal */}
      <AddressSelectionModal 
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        initialAddress={address}
        onConfirm={(newAddr) => {
          setAddress(newAddr);
          setShowAddressModal(false);
          setShowConfirmationModal(true);
        }}
        onBack={() => {
           setShowAddressModal(false);
           setShowVariationModal(true);
        }}
      />

      {/* Confirmation Modal */}
      <ResolutionConfirmationModal 
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleFinalConfirm}
        onEditAddress={() => {
          setShowConfirmationModal(false);
          setShowAddressModal(true);
        }}
        submitting={submitting}
        address={address}
        phone={returnOnboardingData?.shipping_details?.phone}
        returningProduct={{
          image: product.image,
          title: product.title,
          price: product.price,
          color: (productInfo?.variant || "").split(' / ')[0] || "Original",
          size: (productInfo?.variant || "").split(' / ')[1] || "Default"
        }}
        replacementProduct={{
          image: selectedColor?.image || product.image,
          title: selectedColor?.productTitle || product.title,
          price: selectedSolution === "store_credit" ? Math.round(Number(product.price) * 1.05) : Number(product.price),
          color: selectedColor?.name,
          size: selectedSize || ""
        }}
        type={(selectedSolution as any) || 'replacement'}
      />

      {/* Refund Selection Modal */}
      <RefundSelectionModal 
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onAcceptStoreCredit={handleAcceptStoreCredit}
        onAcceptOriginal={handleAcceptOriginalRefund}
        submitting={submitting}
        productPrice={product.price}
      />

      {/* Success Modal */}
      <ResolutionSuccessModal 
        isOpen={successModal.isOpen}
        onClose={goToOrderDetails}
        type={successModal.type}
        amount={successModal.type === 'store_credit' ? Math.round(Number(product.price) * 1.05) : product.price}
        productTitle={product.title}
      />
    </motion.div>
  );
};

export default FixResolution;
